import { ReactNode, useState } from 'react'
import { ChevronDown } from 'lucide-react'

interface NavGroupProps {
  label: string
  children: ReactNode
  icon?: ReactNode
  defaultOpen?: boolean
  /** When true, forces the group open and hides the toggle (e.g. while a search is active). */
  forceOpen?: boolean
}

/**
 * NavGroup - Accordion group for navigation.
 * Auto-opens when a child route is active (via defaultOpen prop computed by caller).
 */
export function NavGroup({ label, children, icon, defaultOpen = false, forceOpen = false }: NavGroupProps) {
  const [open, setOpen] = useState(defaultOpen)
  const isOpen = forceOpen || open

  return (
    <div data-nav-group className="mt-2 border-t border-white/10 first:mt-0 first:border-0 pt-2 first:pt-0">
      <button
        onClick={() => !forceOpen && setOpen(!open)}
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-xs font-semibold tracking-wide text-zinc-500 hover:text-zinc-300 transition-colors"
      >
        <div className="flex items-center gap-2">
          {icon && <span className="text-lg" aria-hidden="true">{icon}</span>}
          <span className="uppercase">{label}</span>
        </div>
        {!forceOpen && (
          <ChevronDown
            className={`h-3 w-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        )}
      </button>

      {isOpen && <div className="space-y-0.5 mt-0.5">{children}</div>}
    </div>
  )
}
